import type { RepeatableBlockStateType } from '@unifyapps/defs/blocks/Repeatable/types';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { RepeatableInfo, RuntimeSourceInfo } from '../../../utils/repeatables/attachInstances';

const EMPTY_RUNTIME_SOURCE_INFO: RuntimeSourceInfo = {
  childToRepeatable: {},
  repeatables: {},
};

interface RuntimeSourceInfoCache {
  lastAncestorMap?: Map<string, string>;
  lastBlocksState?: Record<string, BlockStateUnionType | undefined>;
  lastResult?: RuntimeSourceInfo;
  lastParentStates?: Map<string, RepeatableBlockStateType | undefined>;
}

/**
 * Builds the per-build `{ childToRepeatable, repeatables }` descriptor that
 * powers the `instances` lazy proxies. The factory wraps a closure-scoped
 * cache so consecutive calls reuse the outer record AND per-parent
 * `RepeatableInfo` refs when underlying parent states are reference-stable —
 * keeping ContextBuilder's `canReuseBaseContext` cache hot on 600-block pages.
 *
 * Returns the shared `EMPTY_RUNTIME_SOURCE_INFO` singleton when there are no
 * Repeatables so cache equality stays stable.
 */
export function createBuildRuntimeSourceInfo() {
  const cache: RuntimeSourceInfoCache = {};
  return function buildRuntimeSourceInfo(
    ancestorMap: Map<string, string> | undefined,
    blocksState: Record<string, BlockStateUnionType | undefined>,
  ): RuntimeSourceInfo {
    if (!ancestorMap || ancestorMap.size === 0) {
      return EMPTY_RUNTIME_SOURCE_INFO;
    }

    if (
      cache.lastAncestorMap === ancestorMap &&
      cache.lastBlocksState === blocksState &&
      cache.lastResult
    ) {
      return cache.lastResult;
    }

    // blocksState ref changed but every parent state object is still ===; the
    // result is byte-for-byte identical so return the cached outer record.
    if (cache.lastAncestorMap === ancestorMap && cache.lastResult && cache.lastParentStates) {
      let allParentsStable = true;
      for (const parentBlockId of cache.lastParentStates.keys()) {
        if (blocksState[parentBlockId] !== cache.lastParentStates.get(parentBlockId)) {
          allParentsStable = false;
          break;
        }
      }
      if (allParentsStable) {
        cache.lastBlocksState = blocksState;
        return cache.lastResult;
      }
    }

    const childToRepeatable: Record<string, string | undefined> = {};
    const repeatables: Record<string, RepeatableInfo | undefined> = {};
    const parentStates = new Map<string, RepeatableBlockStateType | undefined>();

    ancestorMap.forEach((parentBlockId, childBlockId) => {
      childToRepeatable[childBlockId] = parentBlockId;

      if (!repeatables[parentBlockId]) {
        const parentState = blocksState[parentBlockId] as RepeatableBlockStateType | undefined;
        const prevParentState = cache.lastParentStates?.get(parentBlockId) as
          | RepeatableBlockStateType
          | undefined;
        const prevEntry = cache.lastResult?.repeatables[parentBlockId];
        if (prevEntry && prevParentState === parentState) {
          // Per-parent ref reuse — load-bearing for isShallowEqualRuntimeSourceInfo.
          repeatables[parentBlockId] = prevEntry;
        } else {
          // Read from runtime-published flat fields. When undefined (first render
          // before the publish effect has run), the Proxy returns length: 0 — the
          // legacy path instead iterated the raw "{{ … }}" expression string char-by-char.
          repeatables[parentBlockId] = {
            data: parentState?.evaluatedData,
            itemPrimaryKeys: parentState?.itemPrimaryKeys,
            itemKeyToIndex: parentState?.itemKeyToIndex,
            primaryKey: parentState?.content?.primaryKey,
          };
        }
        parentStates.set(parentBlockId, parentState);
      }
    });

    const result: RuntimeSourceInfo = { childToRepeatable, repeatables };
    cache.lastAncestorMap = ancestorMap;
    cache.lastBlocksState = blocksState;
    cache.lastResult = result;
    cache.lastParentStates = parentStates;
    return result;
  };
}
